import { MapPin, TrendingUp, Mail, MessageSquare, Activity, Target, Users, BarChart3 } from "lucide-react";

const neighborhoods = [
  { name: "Centro", engagement: 92, color: "bg-success" },
  { name: "Zona Sul", engagement: 87, color: "bg-success" },
  { name: "Asa Norte", engagement: 78, color: "bg-success/80" },
  { name: "Asa Sul", engagement: 74, color: "bg-success/70" },
  { name: "Taguatinga", engagement: 68, color: "bg-highlight" },
  { name: "Ceilândia", engagement: 61, color: "bg-highlight" },
  { name: "Samambaia", engagement: 54, color: "bg-highlight/80" },
  { name: "Planaltina", engagement: 41, color: "bg-destructive/60" },
  { name: "Gama", engagement: 38, color: "bg-destructive/60" },
];

// Heat-map cells (10x6 grid) with intensity 0-100
const heatGrid = [
  90, 85, 78, 72, 88, 92, 80, 65, 55, 48,
  82, 88, 75, 70, 84, 90, 76, 60, 50, 42,
  74, 80, 68, 62, 78, 82, 70, 58, 45, 38,
  65, 72, 60, 55, 70, 76, 62, 50, 40, 32,
  55, 62, 50, 48, 60, 65, 54, 42, 35, 28,
  48, 54, 42, 40, 52, 58, 46, 38, 30, 22,
];

function heatColor(v: number) {
  if (v >= 80) return "oklch(0.68 0.17 152)"; // success
  if (v >= 65) return "oklch(0.78 0.16 130)";
  if (v >= 50) return "oklch(0.86 0.16 92)"; // highlight
  if (v >= 35) return "oklch(0.78 0.18 50)";
  return "oklch(0.65 0.20 28)"; // low
}

export function TerritorioMock() {
  return (
    <div className="rounded-2xl bg-white border border-border shadow-elegant overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/40">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-accent" />
          <span className="text-sm font-bold text-foreground">Inteligência territorial</span>
          <span className="ml-2 text-[10px] uppercase tracking-widest text-success font-bold flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" /> Ao vivo
          </span>
        </div>
        <div className="flex gap-1.5 text-[10px]">
          {[
            { l: "Alto", c: "bg-success" },
            { l: "Médio", c: "bg-highlight" },
            { l: "Baixo", c: "bg-destructive/70" },
          ].map((x) => (
            <span key={x.l} className="flex items-center gap-1 text-muted-foreground">
              <span className={`h-2 w-2 rounded-sm ${x.c}`} /> {x.l}
            </span>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-0">
        {/* Heatmap */}
        <div className="lg:col-span-7 p-5 border-b lg:border-b-0 lg:border-r border-border">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-bold text-foreground">Mapa de calor eleitoral</div>
              <div className="text-[11px] text-muted-foreground">Engajamento por território — últimos 7 dias</div>
            </div>
            <div className="text-xs text-muted-foreground">Brasília / DF</div>
          </div>

          <div className="relative rounded-xl bg-gradient-to-br from-muted/40 to-muted/10 p-4 border border-border">
            <div className="grid grid-cols-10 gap-1.5">
              {heatGrid.map((v, i) => (
                <div
                  key={i}
                  className="aspect-square rounded-md transition-transform hover:scale-110 hover:z-10 cursor-pointer"
                  style={{ backgroundColor: heatColor(v), opacity: 0.3 + v / 130 }}
                  title={`Intensidade ${v}%`}
                />
              ))}
            </div>
            {/* Pin */}
            <div className="absolute top-[18%] left-[28%] flex flex-col items-center">
              <div className="px-2 py-1 rounded-md bg-primary text-primary-foreground text-[10px] font-bold whitespace-nowrap shadow-soft">
                Centro · 92%
              </div>
              <div className="h-3 w-px bg-primary" />
              <div className="h-3 w-3 rounded-full bg-accent border-2 border-white shadow-soft animate-pulse-glow" />
            </div>
          </div>

          {/* Mini KPIs under map */}
          <div className="mt-4 grid grid-cols-4 gap-2">
            {[
              { l: "Bairros monitorados", v: "146", i: MapPin },
              { l: "Abertura e-mail", v: "62%", i: Mail },
              { l: "Leitura WhatsApp", v: "87%", i: MessageSquare },
              { l: "Resposta média", v: "11,4%", i: Activity },
            ].map((k) => (
              <div key={k.l} className="rounded-lg border border-border bg-card p-2.5">
                <k.i className="h-3.5 w-3.5 text-accent mb-1" />
                <div className="text-base font-bold text-foreground leading-none">{k.v}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{k.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Ranking */}
        <div className="lg:col-span-5 p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-sm font-bold text-foreground">Ranking de engajamento</div>
              <div className="text-[11px] text-muted-foreground">Top regiões por interação</div>
            </div>
            <BarChart3 className="h-4 w-4 text-accent" />
          </div>
          <ul className="space-y-2.5">
            {neighborhoods.map((n, i) => (
              <li key={n.name}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="flex items-center gap-2 font-semibold text-foreground">
                    <span className="text-muted-foreground font-mono w-4">{String(i + 1).padStart(2, "0")}</span>
                    {n.name}
                  </span>
                  <span className="font-bold text-foreground">{n.engagement}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full ${n.color} rounded-full`} style={{ width: `${n.engagement}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function TerritorioSection() {
  const segments = [
    { icon: MapPin, t: "Bairros" },
    { icon: Target, t: "Regiões administrativas" },
    { icon: Users, t: "Zonas eleitorais" },
    { icon: TrendingUp, t: "Bases políticas" },
  ];

  const insights = [
    { icon: TrendingUp, t: "Bairros com maior engajamento", c: "text-success" },
    { icon: Activity, t: "Regiões com baixa resposta", c: "text-destructive" },
    { icon: Mail, t: "Áreas com mais aberturas de e-mail", c: "text-accent" },
    { icon: MessageSquare, t: "Locais com mais interações no WhatsApp", c: "text-success" },
    { icon: Target, t: "Pautas mais receptivas por região", c: "text-highlight-foreground" },
    { icon: BarChart3, t: "Comportamento da comunicação por território", c: "text-primary" },
  ];

  const benefits = [
    "Identificação de regiões prioritárias",
    "Ajuste de discurso por bairro",
    "Direcionamento estratégico de equipes",
    "Melhor distribuição de recursos de campanha",
    "Fortalecimento de bases eleitorais",
    "Mapeamento das regiões mais favoráveis",
    "Identificação de áreas que exigem reforço",
  ];

  return (
    <section id="territorio" className="py-24 bg-gradient-to-b from-background to-muted/30 relative overflow-hidden">
      <div className="absolute top-20 right-0 h-[500px] w-[500px] rounded-full bg-accent/10 blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-0 h-[400px] w-[400px] rounded-full bg-success/10 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold uppercase tracking-wider">
            <MapPin className="h-3.5 w-3.5" /> Inteligência territorial
          </div>
          <h2 className="mt-4 text-4xl sm:text-5xl font-bold text-foreground">
            Entenda como cada bairro{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">reage à sua campanha.</span>
          </h2>
          <p className="mt-5 text-lg text-muted-foreground">
            O Vox Campaign acompanha o comportamento político da população por região geográfica, transformando comunicação em <strong className="text-foreground">inteligência estratégica de campanha</strong> em tempo real.
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {segments.map((s) => (
              <div key={s.t} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-border shadow-soft text-xs font-semibold text-foreground">
                <s.icon className="h-3.5 w-3.5 text-accent" />
                {s.t}
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard */}
        <div className="mt-12 relative">
          <div className="absolute -inset-6 bg-gradient-primary opacity-10 blur-3xl rounded-full" />
          <div className="relative">
            <TerritorioMock />
          </div>
        </div>

        {/* Insights grid */}
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {insights.map((it) => (
            <div key={it.t} className="group rounded-2xl bg-card border border-border p-5 hover:border-accent/40 hover:shadow-elegant transition-all">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center group-hover:scale-110 transition-transform">
                  <it.icon className={`h-5 w-5 ${it.c}`} />
                </div>
                <p className="font-semibold text-foreground pt-2">{it.t}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Strategic quote */}
        <div className="mt-14 rounded-3xl bg-gradient-cta text-primary-foreground p-10 lg:p-14 shadow-elegant relative overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="absolute -top-32 -right-20 h-80 w-80 rounded-full bg-accent/40 blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Target className="h-8 w-8 text-highlight mb-4" />
              <p className="text-2xl sm:text-3xl font-bold leading-snug">
                "O Vox Campaign transforma dados de comunicação em{" "}
                <span className="text-highlight">inteligência eleitoral territorial.</span>"
              </p>
              <p className="mt-6 text-primary-foreground/80 italic border-l-2 border-highlight pl-4">
                "Campanhas inteligentes não falam com todos da mesma forma. Elas entendem como cada região responde."
              </p>
            </div>

            <ul className="grid sm:grid-cols-1 gap-2.5">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10 backdrop-blur">
                  <div className="h-6 w-6 rounded-md bg-highlight flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-highlight-foreground text-xs font-bold">✓</span>
                  </div>
                  <span className="text-sm font-medium">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}
